import React, { useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminClinicsListTemplate } from '@/components/templates';
import { ClinicsFilterPanel, ClinicInformationsTableSection } from '@/components/organisms';
import {
  useAdminClinicInformationsList,
  useAdminClinicInformationsCsvDownload,
  type ClinicInformationListParams,
} from '@/hooks/admin/useAdminClinicInformations';
import { getTenantSiteTypes } from '@/services/tenants';
import type { TenantSiteTypeItem } from '@/services/tenants';
import { useSiteAdminAuth } from '@/shared/providers/SiteAdminAuthContext';
import useAppToast from '@/hooks/useAppToast';

const DEFAULT_PER_PAGE = 20;

type SortField =
  | 'clinics.clinic_number'
  | 'clinic_informations.name'
  | 'clinic_informations.available_from';
type SortDir = 'ascend' | 'descend';

type ClinicInfoFilterState = {
  clinicNumber: string;
  clinicName: string;
  availableDate: string;
  siteTypes: string[];
};

const getFiltersFromSearchParams = (searchParams: URLSearchParams): ClinicInfoFilterState => {
  const siteTypesFromArray = searchParams.getAll('site_types').filter(Boolean);
  const siteTypesFromCsv = (searchParams.get('site_types') ?? '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
  const siteTypes = siteTypesFromArray.length > 0 ? siteTypesFromArray : siteTypesFromCsv;

  return {
    clinicNumber: searchParams.get('clinic_number') ?? '',
    clinicName: searchParams.get('clinic_name') ?? '',
    availableDate: searchParams.get('available_date') ?? '',
    siteTypes,
  };
};

const getSortStateFromSearchParams = (
  searchParams: URLSearchParams
): { field: SortField; dir: SortDir } | null => {
  const sortBy = searchParams.get('sort_by');
  const order = searchParams.get('order');
  const isValidSortField = (value: string | null): value is SortField =>
    value === 'clinics.clinic_number' ||
    value === 'clinic_informations.name' ||
    value === 'clinic_informations.available_from';

  if (!isValidSortField(sortBy)) return null;
  if (order !== 'asc' && order !== 'desc') return null;
  return { field: sortBy, dir: order === 'asc' ? 'ascend' : 'descend' };
};

const getPageFromSearchParams = (searchParams: URLSearchParams): number => {
  const raw = Number(searchParams.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
};

const SiteAdminClinicInformationsListPage: React.FC = () => {
  const { site } = useParams<{ site: string }>();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { isLoading: isAuthLoading } = useSiteAdminAuth();
  const toast = useAppToast();

  const isSuperAdminRoute = pathname.startsWith('/super-admin/admin');

  const siteTypesQuery = useQuery({
    queryKey: ['tenants', 'site-types', isSuperAdminRoute ? 'all' : site ?? ''],
    queryFn: () => getTenantSiteTypes(isSuperAdminRoute ? undefined : site ?? undefined),
    enabled: !isAuthLoading && !!site,
    staleTime: Infinity,
  });

  const tenantSiteTypeList: TenantSiteTypeItem[] =
    siteTypesQuery.data?.data?.site_types ?? [];

  const canUseSiteTypeFeatures = tenantSiteTypeList.length > 1;

  const tenantNameBySiteType = useMemo(() => {
    const map = new Map<string, string>();
    tenantSiteTypeList.forEach(t => {
      if (t.site_type) map.set(t.site_type, t.name);
    });
    return map;
  }, [tenantSiteTypeList]);

  useEffect(() => {
    if (siteTypesQuery.isError) {
      toast.error('サイト一覧の取得に失敗しました。');
    }
  }, [siteTypesQuery.isError, toast]);

  const initialFilters = getFiltersFromSearchParams(searchParams);

  const [clinicNumber, setClinicNumber] = useState(initialFilters.clinicNumber);
  const [clinicName, setClinicName] = useState(initialFilters.clinicName);
  const [availableDate, setAvailableDate] = useState(initialFilters.availableDate);
  const [siteTypes, setSiteTypes] = useState<string[]>(initialFilters.siteTypes);

  const [appliedClinicNumber, setAppliedClinicNumber] = useState(initialFilters.clinicNumber);
  const [appliedClinicName, setAppliedClinicName] = useState(initialFilters.clinicName);
  const [appliedAvailableDate, setAppliedAvailableDate] = useState(initialFilters.availableDate);
  const [appliedSiteTypes, setAppliedSiteTypes] = useState<string[]>(initialFilters.siteTypes);

  const tenantSiteTypes = useMemo(
    () => tenantSiteTypeList.map(t => t.site_type).filter(Boolean) as string[],
    [tenantSiteTypeList]
  );
  const hasAllSelected =
    tenantSiteTypes.length > 0 && tenantSiteTypes.every(st => siteTypes.includes(st));

  const [page, setPage] = useState(getPageFromSearchParams(searchParams));
  const [sortState, setSortState] = useState<{ field: SortField; dir: SortDir } | null>(
    getSortStateFromSearchParams(searchParams)
  );

  useEffect(() => {
    const nextFilters = getFiltersFromSearchParams(searchParams);
    const nextPage = getPageFromSearchParams(searchParams);
    const nextSortState = getSortStateFromSearchParams(searchParams);

    setClinicNumber(nextFilters.clinicNumber);
    setClinicName(nextFilters.clinicName);
    setAvailableDate(nextFilters.availableDate);
    setSiteTypes(nextFilters.siteTypes);

    setAppliedClinicNumber(nextFilters.clinicNumber);
    setAppliedClinicName(nextFilters.clinicName);
    setAppliedAvailableDate(nextFilters.availableDate);
    setAppliedSiteTypes(nextFilters.siteTypes);

    setPage(nextPage);
    setSortState(nextSortState);
  }, [searchParams]);

  const params: ClinicInformationListParams = useMemo(
    () => ({
      clinic_number: appliedClinicNumber || undefined,
      clinic_name: appliedClinicName || undefined,
      available_date: appliedAvailableDate || undefined,
      site_types:
        canUseSiteTypeFeatures && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      sort: sortState
        ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc']
        : (['clinic_informations.created_at', 'asc'] as [string, 'asc' | 'desc']),
      page,
      per_page: DEFAULT_PER_PAGE,
    }),
    [
      appliedClinicNumber,
      appliedClinicName,
      appliedAvailableDate,
      appliedSiteTypes,
      canUseSiteTypeFeatures,
      sortState,
      page,
    ]
  );

  const { data, isLoading } = useAdminClinicInformationsList(site ?? null, params);
  const { downloadCsv } = useAdminClinicInformationsCsvDownload();

  const handleSearch = () => {
    if (!site) return;
    const nextParams = new URLSearchParams();
    if (clinicNumber.trim()) nextParams.set('clinic_number', clinicNumber.trim());
    if (clinicName.trim()) nextParams.set('clinic_name', clinicName.trim());
    if (availableDate.trim()) nextParams.set('available_date', availableDate.trim());
    if (canUseSiteTypeFeatures) {
      siteTypes
        .map(v => v.trim())
        .filter(Boolean)
        .forEach(v => nextParams.append('site_types', v));
    }
    nextParams.set('page', '1');
    if (sortState) {
      nextParams.set('sort_by', sortState.field);
      nextParams.set('order', sortState.dir === 'ascend' ? 'asc' : 'desc');
    }

    flushSync(() => {
      setSearchParams(nextParams);
      setPage(1);
      setAppliedClinicNumber(clinicNumber);
      setAppliedClinicName(clinicName);
      setAppliedAvailableDate(availableDate);
      setAppliedSiteTypes(siteTypes);
    });
    void queryClient.refetchQueries({
      queryKey: ['admin', 'clinic-informations', site],
      type: 'active',
    });
  };

  const handleExportCsv = () => {
    if (!site) return;
    downloadCsv(site, {
      clinic_number: appliedClinicNumber || undefined,
      clinic_name: appliedClinicName || undefined,
      available_date: appliedAvailableDate || undefined,
      site_types:
        canUseSiteTypeFeatures && appliedSiteTypes.length > 0 ? appliedSiteTypes : undefined,
      sort: sortState
        ? [sortState.field, sortState.dir === 'ascend' ? 'asc' : 'desc']
        : (['clinic_informations.created_at', 'asc'] as [string, 'asc' | 'desc']),
    });
  };

  const setSort = (field: SortField | null, dir: SortDir | null) => {
    const nextParams = new URLSearchParams(searchParams);

    if (!field || !dir) {
      nextParams.delete('sort_by');
      nextParams.delete('order');
      nextParams.set('page', '1');
      setSearchParams(nextParams);
      return;
    }

    nextParams.set('sort_by', field);
    nextParams.set('order', dir === 'ascend' ? 'asc' : 'desc');
    nextParams.set('page', '1');
    setSearchParams(nextParams);
  };

  const handleChangePage = (nextPage: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams);
  };

  const rows = data?.data?.data ?? [];
  const meta = data?.meta ?? {
    current_page: 1,
    last_page: 1,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
  };

  return (
    <AdminClinicsListTemplate
      breadcrumbItems={[{ title: '治療院情報CSV' }]}
      title="治療院情報CSV"
      filter={
        <ClinicsFilterPanel
          canUseSiteTypeFeatures={canUseSiteTypeFeatures}
          tenants={tenantSiteTypeList}
          siteTypes={siteTypes}
          hasAllSelected={hasAllSelected}
          onChangeSiteTypes={setSiteTypes}
          searchClinicNumber={clinicNumber}
          onChangeSearchClinicNumber={setClinicNumber}
          searchClinicName={clinicName}
          onChangeSearchClinicName={setClinicName}
          availableDate={availableDate}
          onChangeAvailableDate={setAvailableDate}
          onSearch={handleSearch}
          onCsv={handleExportCsv}
        />
      }
      table={
        <ClinicInformationsTableSection
          rows={rows}
          loading={isLoading}
          meta={meta}
          defaultPerPage={DEFAULT_PER_PAGE}
          canUseSiteTypeFeatures={canUseSiteTypeFeatures}
          tenantNameBySiteType={tenantNameBySiteType}
          onSort={(f, d) => setSort(f as SortField | null, d as SortDir | null)}
          onChangePage={handleChangePage}
        />
      }
    />
  );
};

export default SiteAdminClinicInformationsListPage;

